import { action, computed, makeObservable, observable } from 'mobx';

const FONT_FAMILY_KEY = 'terminal-font-family';
const FONT_SIZE_KEY = 'terminal-font-size';

export const TERMINAL_FONT_SIZE_RANGE = { min: 9, max: 24 } as const;
export const DEFAULT_TERMINAL_FONT_SIZE = 12;

/**
 * Fallback stack tried in order by the font engine, per glyph. Common Nerd Font
 * builds first so prompts with powerline/devicon glyphs render if any is
 * installed; "Symbols Nerd Font Mono" is the icons-only companion people install
 * alongside a plain monospace. The system monospace closes it out.
 */
export const DEFAULT_TERMINAL_FONT_STACK = [
  '"JetBrainsMono Nerd Font Mono"',
  '"FiraCode Nerd Font Mono"',
  '"Hack Nerd Font Mono"',
  '"MesloLGS NF"',
  '"Symbols Nerd Font Mono"',
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'monospace',
].join(', ');

function loadFontFamily(): string {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(FONT_FAMILY_KEY)?.trim() ?? '';
}

function clampFontSize(size: number): number {
  return Math.min(
    TERMINAL_FONT_SIZE_RANGE.max,
    Math.max(TERMINAL_FONT_SIZE_RANGE.min, Math.round(size))
  );
}

function loadFontSize(): number {
  if (typeof window === 'undefined') return DEFAULT_TERMINAL_FONT_SIZE;
  const raw = Number(localStorage.getItem(FONT_SIZE_KEY));
  return Number.isFinite(raw) && raw > 0
    ? clampFontSize(raw)
    : DEFAULT_TERMINAL_FONT_SIZE;
}

/** Device-local terminal appearance. Applies to every terminal (local and sandbox). */
class TerminalSettingsStore {
  /** User override; empty string means "use the default stack". */
  fontFamily = loadFontFamily();
  fontSize = loadFontSize();

  constructor() {
    makeObservable(this, {
      fontFamily: observable,
      fontSize: observable,
      resolvedFontFamily: computed,
      setFontFamily: action,
      setFontSize: action,
    });
  }

  /** What xterm actually gets: the override in front of the default stack. */
  get resolvedFontFamily(): string {
    const custom = this.fontFamily.trim();
    if (!custom) return DEFAULT_TERMINAL_FONT_STACK;
    // quote a bare multi-word family name so it survives the CSS font parser
    const quoted =
      /^["']/.test(custom) || !/\s/.test(custom) ? custom : `"${custom}"`;
    return `${quoted}, ${DEFAULT_TERMINAL_FONT_STACK}`;
  }

  setFontFamily(family: string): void {
    this.fontFamily = family.trim();
    if (typeof window === 'undefined') return;
    if (this.fontFamily) localStorage.setItem(FONT_FAMILY_KEY, this.fontFamily);
    else localStorage.removeItem(FONT_FAMILY_KEY);
  }

  setFontSize(size: number): void {
    if (!Number.isFinite(size)) return;
    this.fontSize = clampFontSize(size);
    if (typeof window !== 'undefined') {
      localStorage.setItem(FONT_SIZE_KEY, String(this.fontSize));
    }
  }
}

export const terminalSettingsStore = new TerminalSettingsStore();
